import { Toaster } from '@matillion/component-library'
import { createGetWarehouseComputeResourceMutationData } from 'api/createProjectForm/mutation'
import { WarehouseLookupTypes } from 'api/createProjectForm/types'
import {
  useMutateWarehouseDefaults,
  usePostSecretReference,
  usePostSecretValueV2,
  usePutEnvironment,
  useMutateDatabricksComputeResources
} from 'api/hooks'
import {
  createSecretReferenceMutationData,
  createSecretValueV2MutationData,
  createWarehouseDefaultsMutation,
  updateEnvironmentMutationData
} from 'api/mutation'
import { QueryKey } from 'constants/endpoint'
import { ENVIRONMENT_STORAGE_ITEM } from 'constants/persistance'
import {
  AppRoutes,
  PROJECTS_DEFAULTS,
  PROJECT_DETAILS_ENVIRONMENTS
} from 'constants/route'
import { FormikErrors } from 'formik'
import { useFormNavigation } from 'hooks'
import { steps } from 'modules/Projects/EditEnvironment/EditEnvironmentForm.steps'
import { EditEnvironmentFormikValueTypes } from 'modules/Projects/EditEnvironment/types'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { To, useNavigate, useParams } from 'react-router-dom'
import { AgentsSecretsHost, SecretReferenceTypes, Warehouse } from 'types'

interface FormikSubmitProps {
  values: EditEnvironmentFormikValueTypes
  setValues: (
    values: React.SetStateAction<EditEnvironmentFormikValueTypes>,
    shouldValidate?: boolean
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  ) => Promise<void | FormikErrors<EditEnvironmentFormikValueTypes>>
  skipSecretValueCreation?: boolean
}

export const useEditEnvironmentFormSubmit = () => {
  const navigate = useNavigate()
  const { projectId, envId } = useParams()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { makeToast, clearToasts } = Toaster.useToaster()

  const { isLastStep, nextStep } = useFormNavigation(steps)
  const { mutateAsync: mutateAsyncSecretValueV2 } = usePostSecretValueV2()
  const { mutateAsync: mutateAsyncSecretReference } = usePostSecretReference()
  const { mutateAsync: mutateAsyncWarehouseRoleLookup } =
    useMutateWarehouseDefaults(WarehouseLookupTypes.ROLE, undefined, true)
  const { mutateAsync: mutateAsyncWarehouseDatabaseLookup } =
    useMutateWarehouseDefaults(WarehouseLookupTypes.DATABASE, undefined, true)
  const { mutateAsync: mutateEnvironment } = usePutEnvironment(projectId!)
  const { mutateAsync: mutateAsyncWarehouseComputeResourceLookup } =
    useMutateDatabricksComputeResources({ showErrorDetailMessage: true })

  const navigateToEnvironments = () => {
    window.sessionStorage.removeItem(ENVIRONMENT_STORAGE_ITEM)
    navigate(
      AppRoutes.getProjectDetails(projectId!, PROJECT_DETAILS_ENVIRONMENTS)
    )
  }

  const onSubmit = async ({
    setValues,
    values,
    skipSecretValueCreation = false
  }: FormikSubmitProps) => {
    clearToasts()
    if (isLastStep) {
      await mutateEnvironment({
        values: updateEnvironmentMutationData(values),
        environmentId: envId!
      })
        .then((_res) => {
          makeToast({
            title: t('editEnvironment.responseMessage.success', {
              environmentName: values.environmentName
            }),
            message: '',
            type: 'success'
          })
          queryClient.invalidateQueries([QueryKey.SECRET_REFERENCES, projectId])
          queryClient.invalidateQueries([QueryKey.ENVIRONMENTS_LIST, projectId])
          navigateToEnvironments()
        })
        .catch((_err) => {
          makeToast({
            title: t('editEnvironment.responseMessage.error'),
            message: '',
            type: 'error'
          })
        })
    } else {
      try {
        if (nextStep === PROJECTS_DEFAULTS && !skipSecretValueCreation) {
          let response
          if (
            values.projectConfiguration === AgentsSecretsHost.MatillionHosted
          ) {
            if (values.secretValue) {
              response = await mutateAsyncSecretValueV2({
                values: createSecretValueV2MutationData({
                  ...values,
                  name: values.environmentName,
                  secretValue: {
                    password: values.secretValue
                  }
                })
              })
            }
          } else {
            response = await mutateAsyncSecretReference({
              values: createSecretReferenceMutationData(
                values,
                SecretReferenceTypes.DWH_PASSWORD
              )
            })
          }
          values = {
            ...values,
            ...(response && { secretReferenceId: response.secretId })
          }

          // Ensure that there is a connection to warehouse defaults role in order to continue
          // Otherwise it throws an error which is caught below and does not proceed to the next page
          switch (values.type) {
            case Warehouse.Snowflake:
              await mutateAsyncWarehouseRoleLookup({
                values: createWarehouseDefaultsMutation(values)
              })
              break
            case Warehouse.Redshift:
              await mutateAsyncWarehouseDatabaseLookup({
                values: createWarehouseDefaultsMutation(values)
              })
              break
            case Warehouse.Databricks:
              await mutateAsyncWarehouseComputeResourceLookup({
                values: createGetWarehouseComputeResourceMutationData(values)
              })
              break
          }

          setValues(values)
        }

        navigate(nextStep as To, { replace: true })
      } catch (_e) {
        // handled by axios config
      }
    }
  }

  return onSubmit
}
